import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, api, errorText } from '../api/client';
import { queryKeys, useMe } from '../api/hooks';
import { useT } from '../lib/i18n';
import { forgetNote, outboxFor, queueNote } from '../lib/outbox';

/**
 * Writing a note without going to look for one.
 *
 * **This is the point of the module.** A note that has to be filed before it can
 * be written is a note that gets written somewhere else — so this is one field,
 * reachable from every screen, that asks for nothing but the words: no folder,
 * no tag, no title. `Enter` sends and closes, and the note lands in the inbox,
 * which is where a note with no place yet belongs (D260815b).
 *
 * It is mounted once by `AppShell` rather than per trigger, because it owns two
 * things a second copy would duplicate: the dialog, and the buffer below.
 *
 * **The buffer is an exception to an invariant, and it is guarded as one**
 * (D260815d). If the send fails for want of a network, the note goes into
 * `localStorage` and the interface says it is waiting; an `online` event replays
 * it. Signing out empties it — `useLogout` does that — and every entry carries
 * the account it was written for, which the replay checks against the session
 * before sending. Both, because one guard for "one account's draft reaching the
 * next person on this phone" is one too few.
 */
export function QuickCapture({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): ReactElement | null {
  const t = useT();
  const queryClient = useQueryClient();
  const { data: user } = useMe();
  const username = user?.username ?? null;

  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [waiting, setWaiting] = useState(0);
  const field = useRef<HTMLTextAreaElement | null>(null);

  const countWaiting = useCallback(() => {
    setWaiting(username === null ? 0 : outboxFor(username).length);
  }, [username]);

  /**
   * Sends everything this account has waiting, oldest first.
   *
   * The username is checked against the session here as well as being filtered
   * on: `outboxFor` is the filter, and this is the reason the field exists at
   * all. An entry only leaves the buffer once the server has acknowledged it —
   * a failure leaves it for the next attempt rather than losing it.
   */
  const flush = useCallback(async () => {
    if (username === null) return;

    for (const entry of outboxFor(username)) {
      if (entry.username !== username) continue;
      try {
        await api.notes.create({ body: entry.body });
        forgetNote(entry.id);
      } catch (error) {
        // A refusal is final — the server has answered, and replaying it every
        // time the network flickers would keep a note that cannot be stored for
        // ever. Anything else is the network, and it is worth another try.
        if (error instanceof ApiError) forgetNote(entry.id);
        else break;
      }
    }
    countWaiting();
    queryClient.invalidateQueries({ queryKey: queryKeys.notes }).catch(() => undefined);
  }, [username, countWaiting, queryClient]);

  // On mount for whatever last session left behind, and whenever the connection
  // comes back.
  useEffect(() => {
    countWaiting();
    void flush();

    const onOnline = (): void => void flush();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [countWaiting, flush]);

  useEffect(() => {
    if (open) field.current?.focus();
    else {
      setRefusal(null);
      setBody('');
    }
  }, [open]);

  const send = async (): Promise<void> => {
    const text = body.trim();
    if (text === '' || username === null) return;

    setSending(true);
    setRefusal(null);
    try {
      await api.notes.create({ body: text });
      await queryClient.invalidateQueries({ queryKey: queryKeys.notes });
      onClose();
    } catch (error) {
      // An `ApiError` means the server answered and said no — showing its own
      // sentence is more useful than pretending the note is on its way. Anything
      // else never reached it, and that is what the buffer is for.
      if (error instanceof ApiError) {
        setRefusal(errorText(error, t('notes.captureFailed')));
      } else {
        queueNote(username, text);
        countWaiting();
        onClose();
      }
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    // Still mounted, and still replaying: the buffer has to be emptied whether
    // or not anybody has opened the dialog since the connection came back.
    return waiting > 0 ? (
      <p
        role="status"
        className="fixed bottom-24 left-1/2 z-40 -translate-x-1/2 rounded-full border border-line bg-surface px-4 py-1.5 text-xs text-ink-500 shadow-sm lg:bottom-6"
      >
        {t('notes.waiting', waiting)}
      </p>
    ) : null;
  }

  return (
    <div
      // The overlay closes on a press outside, which is what everybody tries
      // first; `Escape` is on the field below, where the focus is.
      onMouseDown={onClose}
      className="fixed inset-0 z-50 flex items-start justify-center bg-ink-900/20 px-5 pt-24 backdrop-blur-sm"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('notes.capture')}
        onMouseDown={(event) => event.stopPropagation()}
        className="w-full max-w-xl rounded-lg border border-line bg-surface p-3 shadow-lg"
      >
        <textarea
          ref={field}
          value={body}
          rows={4}
          onChange={(event) => setBody(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') onClose();
            // Enter sends; Shift+Enter is a new line. A capture field is one
            // thought long, and having to reach for a button to finish it is
            // what makes people stop using one.
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void send();
            }
          }}
          placeholder={t('notes.capturePlaceholder')}
          aria-label={t('notes.capture')}
          disabled={sending}
          className="w-full resize-none bg-transparent px-2 py-1 text-base outline-none placeholder:text-ink-400"
        />

        <div className="mt-2 flex items-center gap-3 border-t border-line pt-2">
          <p className="flex-1 text-xs text-ink-400">
            {refusal === null ? (
              t('notes.captureHint')
            ) : (
              <span className="text-alert">{refusal}</span>
            )}
          </p>
          <button
            type="button"
            onClick={() => void send()}
            disabled={sending || body.trim() === ''}
            className="min-h-9 rounded-lg bg-accent px-3 text-sm font-semibold text-accent-ink transition-colors hover:bg-accent-dim disabled:opacity-60"
          >
            {t('notes.captureAction')}
          </button>
        </div>
      </div>
    </div>
  );
}
